import { Form, FormDescription, FormField, FormGroup, FormItem, FormLabel, FormMessage } from "@/components/core/forms/form.tsx";
import { Input } from "@/components/core/forms/input";
import Select from "@/components/core/forms/select";
import caseEvidenceService from "@/features/case-evaluation/api/services/evidence-service";
import type { CaseActivity } from "@/features/case-evaluation/types/activities";
import { caseStagesQueryOptions } from "@/features/case-record/api/queries/case-stage-query";
import type { CaseRecord } from "@/features/case-record/types/case-records";
import eventService from "@/features/dashboard/api/services/event-service";
import { IconAlertCircle, IconCirclePlus, IconTrash } from "@tabler/icons-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import Alert from "../core/alert";
import { Button } from "../core/buttons/button";
import TagsInput from "../core/forms/tags-input";
import { useModalControl } from "../core/modals/use-modal-controller";
import Tooltip from "../core/tooltip";

interface HearingRow {
	title: string;
	assigned_to: string;
	remarks: string;
	due_date: string;
	stage: string;
	hearing_mode: string;
	exhibits: string;
	hearing_count: number;
}

export default function CaseHearingModal({
	caseClassification,
	data,
	caseData,
	stage,
	handleSubmit,
}: {
	data: CaseActivity;
	caseData: CaseRecord;
	caseClassification?: string;
	stage?: string;
	handleSubmit: (values: {
		hearings: HearingRow[];
	}) => void;
}) {
	const caseStageQuery = useSuspenseQuery(
		caseStagesQueryOptions({
			case_classification: caseClassification ?? "",
			status: "",
		}),
	);
	const caseStagesNew = caseStageQuery.data;
	const { closeModal } = useModalControl();
	const form = useForm({
		defaultValues: {
			hearings: [
				{
					title: data?.activity || "Hearing",
					type: data?.activity || "Hearing",
					assigned_to: "Both",
					remarks: data?.remarks || "",
					due_date: data?.due_at || "",
					stage: data?.stage || stage,
					hearing_mode: data?.hearing_mode || "",
					exhibits: data?.exhibits || "",
				},
			],
		},
	});
	const hearings = useFieldArray({
		control: form.control,
		name: "hearings",
	});

	const [debouncedDate, setDebouncedDate] = useState("");
	const [parties, setParties] = useState<string>();
	const [isHoliday, setIsHoliday] = useState(false);
	const [hasDuplicateDate, setHasDuplicateDate] = useState(false);
	const [rowCounts, setRowCount] = useState(0);

	const hasDuplicateDates = (dates: string[]) => {
		const seen = new Set<string>();
		for (const date of dates.filter(Boolean).map((d) => d.slice(0, 10))) {
			if (seen.has(date)) return true;
			seen.add(date);
		}
		return false;
	};

	useEffect(() => {
		const subscription = form.watch((value) => {
			const dates = value.hearings?.map((h) => h.due_date) || [];
			setHasDuplicateDate(hasDuplicateDates(dates));
		});
		return () => subscription.unsubscribe();
	}, [form]);

	const handleAddRow = () => {
		hearings.append({
			title: "Hearing",
			type: "Hearing",
			assigned_to: "Both",
			remarks: "",
			due_date: "",
			stage: stage || "",
			hearing_mode: "",
			exhibits: "",
		});
	};

	const hearingDate = form.watch("hearings").map((hearing) => {
		return hearing.due_date;
	});

	const hearingRemarks = form.watch("hearings").map((hearing) => {
		return hearing.remarks;
	});

	const updateDebouncedDate = useCallback(
		debounce(
			(newDate) => {
				setDebouncedDate(newDate);
			},
			500,
			{ trailing: true },
		),
		[],
	);

	useEffect(() => {
		if (hearingDate) {
			const dates = Array.isArray(hearingDate) ? hearingDate : [hearingDate];

			let hasEmptyDate = false;

			dates.map((data) => {
				if (!data || data.trim() === "") {
					hasEmptyDate = true;
				} else {
					const parsedDate = new Date(data);
					if (!Number.isNaN(parsedDate.getTime())) {
						updateDebouncedDate(format(parsedDate, "yyyy-MM-dd"));
					}
				}
			});
			if (hasEmptyDate) {
				setRowCount(hearings.fields.length - 1);
			} else {
				setRowCount(hearings.fields.length);
			}
		}
	}, [hearingDate, updateDebouncedDate]);

	useEffect(() => {
		if (debouncedDate) {
			eventService
				.validateHolidays(debouncedDate)
				.then((response) => {
					if (response.id) {
						setIsHoliday(true);
					} else {
						setIsHoliday(false);
					}
				})
				.catch((_) => {
					setIsHoliday(false);
				});
		}
	}, [debouncedDate, hearingDate]);

	console.log(parties, "parties jalnslakj");

	return (
		<Form {...form}>
			<form className="w-full" onSubmit={form.handleSubmit(handleSubmit)}>
				<div className="flex flex-col w-full gap-4">
					{isHoliday && (
						<div className="col-span-12 w-full py-1">
							<Alert type="info" allowClose={false} title="The date selected is a Holiday" icon={<IconAlertCircle size={20} stroke={2} />}>
								The selected date falls on a holiday. Please take this into consideration when scheduling this hearing. You can still schedule a hearing on this
								date.
							</Alert>
						</div>
					)}
					{hasDuplicateDate && (
						<Alert type="error" allowClose={false} title="You’ve selected the same date more than once." icon={<IconAlertCircle size={20} stroke={2} />}>
							Duplicate hearing dates found. Please ensure each hearing has a different date to avoid conflicts.
						</Alert>
					)}
					<div className="flex flex-col gap-4">
						{hearings.fields.map((hearing, index) => (
							<div className="border rounded-md w-full p-4" key={index}>
								<div className="flex flex-col">
									<div className="flex flex-row">
										<div className="flex flex-col w-full gap-3">
											<div className="flex flex-row items-center justify-between">
												<div className="flex flex-col w-full">
													<span className="text-lg font-semibold text-foreground-dark">Hearing # {index + 1}</span>
												</div>
												<button
													className="inline-flex justify-center items-center px-3 text-danger-600 hover:text-danger-500"
													type="button"
													onClick={() => hearings.remove(index)}
												>
													<IconTrash size={18} stroke={2} />
												</button>
											</div>
											<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
												<div className="col-span-2 w-full">
													<FormField
														control={form.control}
														name={`hearings[${index}].due_date`}
														render={({ field }) => (
															<FormItem>
																<div className="flex flex-col w-full">
																	<FormLabel required>Date and Time</FormLabel>
																	<FormDescription />
																</div>
																<FormGroup>
																	<Input
																		type="datetime-local"
																		onChange={async (e) => {
																			const value = e.target.value;
																			const response = await eventService.validateHearing(value, caseData.id);
																			form.setValue(`hearings.${index}.hearing_count`, response);
																			field.onChange(value);
																		}}
																	/>
																</FormGroup>
																<FormMessage />
															</FormItem>
														)}
													/>
												</div>
												<div className="col-span-3 w-full">
													<FormField
														control={form.control}
														name={`hearings[${index}].remarks`}
														render={({ field }) => (
															<FormItem>
																<div className="flex flex-col w-full">
																	<FormLabel required>Activity/Event</FormLabel>
																	<FormDescription />
																</div>
																<FormGroup>
																	<Input autoComplete="off" placeholder="e.g Presentation of Evidence" {...field} />
																</FormGroup>
																<FormMessage />
															</FormItem>
														)}
													/>
												</div>
												<div className="col-span-3 w-full">
													<FormField
														control={form.control}
														name={`hearings[${index}].stage`}
														render={({ field }) => (
															<FormItem>
																<div className="flex flex-col w-full">
																	<FormLabel required>Stage</FormLabel>
																	<FormDescription />
																</div>
																<FormGroup>
																	<Select
																		onValueChange={async (value) => {
																			field.onChange(value);
																		}}
																		defaultValue={field.value}
																		options={caseStagesNew.data
																			.filter((c) => c.is_visible)
																			.map((c) => ({
																				label: c.label,
																				value: c.name,
																			}))}
																	/>
																</FormGroup>
															</FormItem>
														)}
													/>
												</div>

												<div className="col-span-2 w-full">
													<FormField
														control={form.control}
														name={`hearings[${index}].hearing_mode`}
														render={({ field }) => (
															<FormItem>
																<div className="flex flex-col w-full">
																	<FormLabel required>Hearing Mode</FormLabel>
																	<FormDescription />
																</div>
																<FormGroup>
																	<Select
																		onValueChange={async (value) => {
																			field.onChange(value);
																		}}
																		defaultValue={field.value}
																		options={[
																			{
																				value: "Face to Face",
																				label: "Face to Face",
																			},
																			{
																				value: "Online",
																				label: "Online",
																			},
																		]}
																	/>
																</FormGroup>
																<FormMessage />
															</FormItem>
														)}
													/>
												</div>
												<div className="col-span-2 w-full">
													<FormField
														control={form.control}
														name={`hearings.${index}.hearing_count`}
														render={({ field }) => {
															return (
																<FormItem>
																	<div className="flex flex-col w-full">
																		<FormLabel>Settings</FormLabel>
																		<FormDescription />
																	</div>
																	<FormGroup>
																		<Tooltip className="z-[999]" content="Click to open calendar in new tab" side="top">
																			{/* <button
                                      onClick={() => {
                                        window.open(`/calendar?view=true&date=${form.getValues(`hearings.${index}.due_date`)}`);
                                      }}
                                      type="button"
                                      className="text-start border w-full rounded-md block bg-muted text-muted-foreground px-3"
                                    > */}
																			<Button
																				className="w-full !justify-start !containerbg-muted text-muted-foreground"
																				variant="ghost"
																				onClick={() => {
																					window.open(`/calendar?view=true&date=${form.getValues(`hearings.${index}.due_date`)}`);
																				}}
																			>
																				{field.value || 0}
																			</Button>
																			{/* </button> */}
																		</Tooltip>
																	</FormGroup>
																</FormItem>
															);
														}}
													/>
												</div>

												<div className="col-span-4 w-full">
													<FormField
														control={form.control}
														name={`hearings[${index}].witness_name`}
														render={({ field }) => (
															<FormItem>
																<div className="flex flex-col w-full">
																	<FormLabel>Witness /Judicial Affidavits</FormLabel>
																	<FormDescription />
																</div>
																<FormGroup>
																	<TagsInput
																		closeMenuOnSelect={false}
																		isMulti={true}
																		defaultValue={field.value}
																		onValueChange={(value) => {
																			field.onChange(value);
																			if (value.length > 0) {
																				setParties(value[0].parties);
																			}
																		}}
																		loadOptions={async (value: string) => {
																			const items = await caseEvidenceService.listAllByCaseId(caseData.id, {
																				"filter[evidence_type]": "Testimonial",
																				"filter[case_id]": caseData.id,
																				"filter[search]": value,
																			});
																			return items.data.map((item) => ({
																				label: item.witness_name,
																				value: item.id,
																				parties: item.parties,
																				case_record_user_id: item.case_record_user_id || null,
																			}));
																		}}
																	/>
																</FormGroup>
															</FormItem>
														)}
													/>
												</div>

												<div className="col-span-8 w-full">
													<FormField
														control={form.control}
														name={`hearings[${index}].exhibits`}
														render={() => (
															<FormItem>
																<div className="flex flex-col w-full">
																	<FormLabel>Exhibits</FormLabel>
																	<FormDescription />
																</div>
																<FormGroup>
																	<FormField
																		control={form.control}
																		name={`hearings[${index}].exhibits`}
																		render={({ field }) => (
																			<TagsInput
																				closeMenuOnSelect={false}
																				key={parties}
																				className="break-words"
																				isMulti={true}
																				defaultValue={field.value}
																				onValueChange={(value) => {
																					field.onChange(value);
																				}}
																				loadOptions={async (value: string) => {
																					const items = await caseEvidenceService.listAllByCaseId(caseData.id, {
																						"filter[case_id]": caseData.id,
																						"filter[parties]": parties,
																						"filter[search]": value,
																					});
																					return items.data
																						.filter((i) => i.evidence_type !== "Testimonial" && !i.parent_id)
																						.map((item) => ({
																							label: `${item.exhibit} - ${item.description}`,
																							value: item.id,
																							parties: item.parties,
																							witness_name: item.witness_name || null,
																							case_record_user_id: item.case_record_user_id || null,
																						}));
																				}}
																			/>
																		)}
																	/>
																</FormGroup>
															</FormItem>
														)}
													/>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						))}
					</div>
					<Button type="button" onClick={handleAddRow} size="default" variant="ghost">
						<IconCirclePlus size={18} stroke={2} /> Add New Schedule
					</Button>
					<div className="flex flex-row justify-between w-full">
						<Button
							className=""
							variant="ghost"
							type="button"
							onClick={() => {
								closeModal();
							}}
						>
							Cancel
						</Button>
						<Button type="submit" disabled={form.formState.isSubmitting || hasDuplicateDate} loading={form.formState.isSubmitting} variant="default">
							Confirm
						</Button>
					</div>
				</div>
			</form>
		</Form>
	);
}
